import { createAsyncThunk, isFulfilled, isPending } from "@reduxjs/toolkit";
import axios from "axios";
import { IPaginationResponse } from "shared/util/pagination.constants";
import {
  IEdoChatMessage,
  defaultValue
} from "../../model/edo-chat-message.model";
import { cleanEntity } from "../../util/entity-utils";
import {
  EntityState,
  IQueryParamsWithFilters,
  createEntitySlice,
  serializeAxiosError
} from "../reducer.utils";
import { getParticipationsByUser } from "./edo-chat-participation.reducer";

const initialState: EntityState<IEdoChatMessage> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
  unseenMessages: 0,
};

const apiUrl = "/chat-messages";

// Actions

export const getEntities = createAsyncThunk(
  "edoChatMessage/fetch_entity_list",
  async ({ page, size, sort, filters }: IQueryParamsWithFilters) => {
    const params = new URLSearchParams(filters);
    if (sort) {
      params.append("page", String(page));
      params.append("limit", String(size));
      if (typeof sort === "string") {
        params.append("sortBy", sort.replace(",", ":"));
      } else {
        for (let s of sort) {
          params.append("sortBy", s.replace(",", ":"));
        }
      }
    }
    let requestUrl;
    if (params.toString() === "") {
      requestUrl = `${apiUrl}`;
    } else {
      requestUrl = `${apiUrl}?${params.toString()}`;
    }
    return axios.get<IPaginationResponse<IEdoChatMessage>>(requestUrl);
  }
);

export const getEntity = createAsyncThunk(
  "edoChatMessage/fetch_entity",
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IEdoChatMessage>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  "edoChatMessage/create_entity",
  async (entity: IEdoChatMessage, thunkAPI) => {
    const result = await axios.post<IEdoChatMessage>(
      apiUrl,
      cleanEntity(entity)
    );
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  "edoChatMessage/update_entity",
  async (entity: IEdoChatMessage, thunkAPI) => {
    const result = await axios.put<IEdoChatMessage>(
      `${apiUrl}/${entity.id}`,
      cleanEntity(entity)
    );
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  "edoChatMessage/partial_update_entity",
  async (entity: IEdoChatMessage, thunkAPI) => {
    const result = await axios.patch<IEdoChatMessage>(
      `${apiUrl}/${entity.id}`,
      cleanEntity(entity)
    );
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  "edoChatMessage/delete_entity",
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IEdoChatMessage>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const checkAllMessagesByChat = createAsyncThunk(
  "edoChatMessage/check_all_messages",
  async (chatId: string | number, thunkAPI) => {
    const requestUrl = `/chats/${chatId}/messages/check-all-before`;
    const result = await axios.post(requestUrl);
    thunkAPI.dispatch(
      getParticipationsByUser({
        page: 0,
        size: 1000,
        sort: "id:ASC",
      })
    );
    thunkAPI.dispatch(getUnseenMessagesCount());
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const getUnseenMessagesCount = createAsyncThunk(
  "edoChaMessage/get_unseen_messages_count",
  async (thunkApi) => {
    const requestUrl = "/account/messages/count";
    const result = await axios.get(requestUrl);
    return result;
  }
);

// slice

export const EdoChatMessageSlice = createEntitySlice({
  name: "edoChatMessage",
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getUnseenMessagesCount.fulfilled, (state, action) => {
        state.unseenMessages = action.payload.data.count;
      })
      .addCase(deleteEntity.fulfilled, (state) => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const {
          data: {
            data,
            meta: { totalItems },
          },
          headers,
        } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data,
          totalItems,
        };
      })
      .addMatcher(
        isFulfilled(createEntity, updateEntity, partialUpdateEntity),
        (state, action) => {
          state.updating = false;
          state.loading = false;
          state.updateSuccess = true;
          state.entity = action.payload.data;
        }
      )
      .addMatcher(
        // isPending(getEntities, getEntity, getUnseenMessagesCount),
        isPending(getEntities, getEntity),
        (state) => {
          state.errorMessage = null;
          state.updateSuccess = false;
          state.loading = true;
        }
      )
      .addMatcher(
        isPending(
          createEntity,
          updateEntity,
          partialUpdateEntity,
          deleteEntity
        ),
        (state) => {
          state.errorMessage = null;
          state.updateSuccess = false;
          state.updating = true;
        }
      );
  },
});

export const { reset } = EdoChatMessageSlice.actions;

// Reducer
export default EdoChatMessageSlice.reducer;
